import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCcw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { handleError } from "@/lib/errorHandler";
import { captureException } from "@/integrations/posthog/client";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    handleError(error, {
      context: "app_crash",
      category: "crash",
      details: { componentStack: errorInfo.componentStack?.substring(0, 2000) },
    });
    captureException(error, {
      componentStack: errorInfo.componentStack?.substring(0, 2000),
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div 
        className="min-h-screen flex items-center justify-center bg-gradient-to-b from-red-50 via-orange-50 to-white px-8"
        dir="rtl"
      >
        <div className="max-w-md w-full bg-card rounded-2xl shadow-lg p-8 text-center animate-fade-in">
          <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-destructive" />
          </div>
          
          <h1 className="text-2xl font-bold text-foreground mb-3">
            אופס! משהו השתבש
          </h1>
          
          <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
            אנחנו מתנצלים, אבל נתקלנו בבעיה לא צפויה. אל דאגה - הנתונים שלך בטוחים.
          </p>
          
          {this.state.error && (
            <div className="bg-muted/50 rounded-lg p-4 mb-6 text-right">
              <p className="text-xs text-muted-foreground font-mono break-all">
                {this.state.error.message}
              </p>
            </div>
          )}
          
          <div className="flex flex-col gap-3">
            <Button
              onClick={this.handleReload}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              size="lg"
            >
              <RefreshCcw className="w-4 h-4 ml-2" />
              טעינה מחדש של הדף
            </Button>
            
            <Button
              onClick={this.handleReset}
              variant="outline"
              className="w-full"
              size="lg"
            >
              <Home className="w-4 h-4 ml-2" />
              חזרה לדף הבית
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground mt-6">
            אם הבעיה נמשכת, נסו להתנתק ולהתחבר מחדש
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
